export const BACKEND_TRANSACTION_STATUSES = [
  "under_review",
  "pending",
  "request_approved",
  "offer_sent",
  "offer_accepted",
  "agreed",
  "awaiting_funding",
  "awaiting_update",
  "rejected",
  "failed",
  "canceled",
  "declined",
  "expired",
  "completed",
] as const;

export type BackendTransactionStatus =
  (typeof BACKEND_TRANSACTION_STATUSES)[number];

export interface TransactionStatusConfig {
  key: string;
  label: string;
  bg: string;
  text: string;
}

/**
 * Standard configuration for all supported transaction and OTC transaction statuses.
 */
export const TRANSACTION_STATUS_CONFIG_MAP: Record<
  BackendTransactionStatus,
  TransactionStatusConfig
> = {
  under_review: {
    key: "under_review",
    label: "Under review",
    bg: "bg-[#EBE4FF]",
    text: "text-[#5E01D6]",
  },
  pending: {
    key: "pending",
    label: "Pending",
    bg: "bg-[#F2F4F7]",
    text: "text-[#344054]",
  },
  request_approved: {
    key: "request_approved",
    label: "Request approved",
    bg: "bg-[#DCFDDB]",
    text: "text-[#188B18]",
  },
  offer_sent: {
    key: "offer_sent",
    label: "Offer sent",
    bg: "bg-[#FFEFC6]",
    text: "text-[#B64607]",
  },
  offer_accepted: {
    key: "offer_accepted",
    label: "Offer accepted",
    bg: "bg-[#F4EBFF]",
    text: "text-[#6941C6]",
  },
  agreed: {
    key: "agreed",
    label: "Agreed",
    bg: "bg-[#EFF8FF]",
    text: "text-[#175CD3]",
  },
  awaiting_funding: {
    key: "awaiting_funding",
    label: "Awaiting funding",
    bg: "bg-[#FFFAEB]",
    text: "text-[#B54708]",
  },
  awaiting_update: {
    key: "awaiting_update",
    label: "Awaiting update",
    bg: "bg-[#FFFAEB]",
    text: "text-[#B54708]",
  },
  rejected: {
    key: "rejected",
    label: "Rejected",
    bg: "bg-[#FEF3F2]",
    text: "text-[#B42318]",
  },
  failed: {
    key: "failed",
    label: "Failed",
    bg: "bg-[#FEF3F2]",
    text: "text-[#B42318]",
  },
  canceled: {
    key: "canceled",
    label: "Canceled",
    bg: "bg-[#F2F4F7]",
    text: "text-[#667085]",
  },
  declined: {
    key: "declined",
    label: "Declined",
    bg: "bg-[#FEE4E2]",
    text: "text-[#D92D20]",
  },
  expired: {
    key: "expired",
    label: "Expired",
    bg: "bg-[#F2F4F7]",
    text: "text-[#475467]",
  },
  completed: {
    key: "completed",
    label: "Completed",
    bg: "bg-[#ECFDF3]",
    text: "text-[#027A48]",
  },
};

/**
 * Normalizes any status string into a lowercase snake_case identifier.
 */
export const normalizeTransactionStatus = (
  status?: string | null
): string => {
  if (!status) return "";
  return status
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
};

/**
 * Converts a raw or unrecognized status string to a readable title case string.
 */
const humanizeStatus = (status: string): string => {
  return status
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

/**
 * Resolves full badge styling and human-readable label for a given transaction status.
 * Supports exact backend statuses (snake_case), space-separated equivalents, and legacy aliases.
 */
export const getTransactionStatusConfig = (
  status?: string | null
): TransactionStatusConfig => {
  const normalized = normalizeTransactionStatus(status);

  // Exact matches from backend status dictionary
  if (normalized in TRANSACTION_STATUS_CONFIG_MAP) {
    return TRANSACTION_STATUS_CONFIG_MAP[
      normalized as BackendTransactionStatus
    ];
  }

  // Alias lookups
  if (
    normalized === "success" ||
    normalized === "successful" ||
    normalized === "completed_transfer"
  ) {
    return TRANSACTION_STATUS_CONFIG_MAP.completed;
  }

  if (normalized === "submitted" || normalized === "pending_review") {
    return TRANSACTION_STATUS_CONFIG_MAP.under_review;
  }

  if (normalized === "approved") {
    return TRANSACTION_STATUS_CONFIG_MAP.request_approved;
  }

  if (normalized === "awaiting_settlement") {
    return TRANSACTION_STATUS_CONFIG_MAP.offer_sent;
  }

  if (normalized === "cancelled") {
    return TRANSACTION_STATUS_CONFIG_MAP.canceled;
  }

  if (normalized === "flagged") {
    return {
      key: "flagged",
      label: "Flagged",
      bg: "bg-[#FFFAEB]",
      text: "text-[#B54708]",
    };
  }

  if (normalized === "processing") {
    return {
      key: "processing",
      label: "Processing",
      bg: "bg-[#F4EBFF]",
      text: "text-[#6941C6]",
    };
  }

  // Fallback for unknown statuses
  return {
    key: normalized || "unknown",
    label: status ? humanizeStatus(status) : "Unknown",
    bg: "bg-[#F2F4F7]",
    text: "text-[#475467]",
  };
};

/**
 * Returns the human-readable display label for any status.
 */
export const formatTransactionStatus = (
  status?: string | null
): string => {
  return getTransactionStatusConfig(status).label;
};

/**
 * Helper to determine if a status represents a terminal failed or rejected state.
 */
export const isTerminalFailedStatus = (
  status?: string | null
): boolean => {
  const norm = normalizeTransactionStatus(status);
  return (
    norm === "failed" ||
    norm === "rejected" ||
    norm === "declined" ||
    norm === "canceled" ||
    norm === "cancelled" ||
    norm === "expired"
  );
};

